import React from "react";

import { TabContainer } from "@companion-core/shared/app/Components/TabContainer";
import { DynamicContent } from "@companion-core/shared/app/Components/Blocks/DynamicContent";
import { BlockContainer, PageContainer } from "@companion-core/shared/app/Components/DynamicPage";
import { useDynamicPageStepper } from "@companion-core/shared/app/Components/DynamicPage/hooks/useDynamicPageContent";
import { RedirectToNotFound } from "@companion-core/shared/app/Components/DynamicPage/RedirectToNotFound/RedirectToNotFound";
import { useScreenFocusEffect } from "@companion-core/shared/app/Components/DynamicPage/hooks/dynamicPageHooks";
import { useHeaderStore } from "@companion-core/shared/app/Store/header";

/**
 * Renders a dynamic page one block at a time as a wizard/stepper (used for the
 * onboarding flow), instead of stacking every block like the regular
 * `DynamicPage`.
 *
 * All the logic lives in the `useDynamicPageStepper` hook: it resolves the
 * current block from the per-slug step index held in `dynamicPageStepperStore`,
 * and exposes `onGoToNext` / `onGoToPrevious` to move through the flow. Only the
 * block at the current step is mounted; navigating advances the stored index.
 *
 * The step position is tracked per page slug, so each stepper page keeps its
 * own progress independently (see `dynamicPageStepperStore`).
 */
export const DynamicPageStepper: React.FC = () => {
  const {
    lang,
    isPending,
    ensureContentAvailable,
    block,
    onGoToPrevious,
    onGoToNext,
    shouldRedirectToNotFound,
  } = useDynamicPageStepper();

  const {
    setIsProfileMenuIconVisible,
    setShouldHideOnScroll,
    setIsNavBarVisible,
    setIsLogoClickable,
  } = useHeaderStore();

  useScreenFocusEffect(() => {
    setIsProfileMenuIconVisible(false);
    setShouldHideOnScroll(false);
    setIsNavBarVisible(false);
    setIsLogoClickable(false);

    return () => {
      setIsProfileMenuIconVisible(true);
      setShouldHideOnScroll(true);
      setIsNavBarVisible(true);
      setIsLogoClickable(true);
    };
  });

  if (shouldRedirectToNotFound) {
    return <RedirectToNotFound />;
  }

  if (!ensureContentAvailable()) {
    return null;
  }

  return (
    <PageContainer
      pageContainsVerticalList={false}
      pageUsesFillLayout
      shouldHideHeaderOnScroll={false}>
      {!isPending && block && (
        <>
          <BlockContainer key={`block-${block.id}`} fillLayout>
            {block.type === "tab-container" ? (
              <TabContainer name={block.slug ?? ""} tabBlocks={block.subBlocks ?? []} />
            ) : (
              <DynamicContent
                type={block.type}
                contentProps={{
                  id: block.id,
                  slug: block.slug,
                  lang: lang,
                  displayType: block.display_type,
                }}
                stepperGoToNext={onGoToNext}
                stepperGoToPrevious={onGoToPrevious}
              />
            )}
          </BlockContainer>
        </>
      )}
    </PageContainer>
  );
};
